import React, { useState } from 'react';
import { useCollectionAgencies, useStartRun, StartRunInput } from '../../hooks/useCaapApi';

interface StartRunModalProps {
  open: boolean;
  onClose: () => void;
  onRunStarted: (runId: number) => void;
}

export const StartRunModal: React.FC<StartRunModalProps> = ({ open, onClose, onRunStarted }) => {
  const [maxStepNumber, setMaxStepNumber] = useState<6 | 7>(6);
  const [isExecuteMode, setIsExecuteMode] = useState(false);
  const [collectionAgencyId, setCollectionAgencyId] = useState<number | undefined>(undefined);
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: agencies } = useCollectionAgencies();
  const startRun = useStartRun();

  const needsAgency = maxStepNumber === 7;
  const canSubmit =
    !startRun.isPending &&
    (!needsAgency || collectionAgencyId != null);

  function handleSubmit() {
    if (isExecuteMode && !showConfirm) {
      setShowConfirm(true);
      return;
    }
    const input: StartRunInput = {
      maxStepNumber,
      isExecuteMode,
      collectionAgencyId: needsAgency ? collectionAgencyId : undefined,
    };
    startRun.mutate(input, {
      onSuccess: (data) => {
        resetForm();
        onRunStarted(data.RunID);
      },
    });
  }

  function resetForm() {
    setMaxStepNumber(6);
    setIsExecuteMode(false);
    setCollectionAgencyId(undefined);
    setShowConfirm(false);
  }

  function handleClose() {
    if (startRun.isPending) return;
    resetForm();
    startRun.reset();
    onClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40" onClick={handleClose} />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md rounded-lg bg-surface-alt p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-primary mb-4">Start New Process Run</h2>

        {/* Confirmation overlay */}
        {showConfirm && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-surface-alt/95 p-6">
            <div className="text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-primary mb-2">Confirm Execute Mode</h3>
              <p className="text-sm text-secondary mb-4">
                This will <strong>execute</strong> the collection assignment process and <strong>modify account data</strong>.
                {maxStepNumber === 7 && ' Step 7 will assign accounts to the selected collection agency.'}
                {' '}This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="rounded-md border border-border-default bg-surface-alt px-4 py-2 text-sm font-medium text-secondary hover:bg-surface-hover"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={startRun.isPending}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {startRun.isPending ? 'Starting...' : 'Yes, Execute'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step Range */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-secondary mb-1">Step Range</label>
          <select
            value={maxStepNumber}
            onChange={(e) => setMaxStepNumber(Number(e.target.value) as 6 | 7)}
            className="w-full rounded-md border border-border-default bg-surface-alt text-primary px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value={6}>Steps 1-6 (Write-Off Only)</option>
            <option value={7}>Steps 1-7 (Full Assignment)</option>
          </select>
        </div>

        {/* Collection Agency — only when Step 7 */}
        {needsAgency && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-secondary mb-1">Collection Agency</label>
            <select
              value={collectionAgencyId ?? ''}
              onChange={(e) => setCollectionAgencyId(e.target.value ? Number(e.target.value) : undefined)}
              className="w-full rounded-md border border-border-default bg-surface-alt text-primary px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select an agency...</option>
              {agencies?.map((a) => (
                <option key={a.ID} value={a.ID}>{a.Name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Mode Toggle */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-secondary mb-2">Mode</label>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => setIsExecuteMode(false)}
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                !isExecuteMode
                  ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                  : 'border-border-default bg-surface-alt text-secondary hover:bg-surface-hover'
              }`}
            >
              Preview
              <p className="text-xs font-normal mt-0.5 opacity-70">Dry run — no changes</p>
            </button>
            <button
              type="button"
              onClick={() => setIsExecuteMode(true)}
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                isExecuteMode
                  ? 'border-red-600 bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'
                  : 'border-border-default bg-surface-alt text-secondary hover:bg-surface-hover'
              }`}
            >
              Execute
              <p className="text-xs font-normal mt-0.5 opacity-70">Modifies accounts</p>
            </button>
          </div>
        </div>

        {/* Error */}
        {startRun.isError && (
          <div className="mb-4 rounded-md bg-red-50 dark:bg-red-950 p-3 text-sm text-red-700 dark:text-red-300">
            Failed to start run: {(startRun.error as Error).message}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={startRun.isPending}
            className="rounded-md border border-border-default bg-surface-alt px-4 py-2 text-sm font-medium text-secondary hover:bg-surface-hover"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
              isExecuteMode
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {startRun.isPending ? 'Starting...' : 'Start Run'}
          </button>
        </div>
      </div>
    </div>
  );
};
